import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Button } from 'devextreme-react/button';
import DateBox from 'devextreme-react/date-box';
import { LoadPanel } from 'devextreme-react/load-panel';
import notify from 'devextreme/ui/notify';
import AccordionReact from 'devextreme-react/accordion';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from 'devextreme/data/data_source';
import {
  Column,
  DataGrid,
  FilterRow,
  HeaderFilter,
  Sorting,
  Paging,
  Pager,
  Export,
  ColumnChooser,
  ColumnChooserSearch,
  MasterDetail,
  Lookup,
  StateStoring,
  Toolbar as GridToolbar,
  Item as GridToolbarItem,
} from 'devextreme-react/data-grid';
import data from '../../assets/apiRoutes.json';
import FormField from '../../dynamicFormBuilder/FormField';
import { GridLayout } from '../../common/GridLayout';
import GridLayoutManager from '../../gridLayoutCustomization/GridLayoutManager';
import { sanitizeHtml, withLineBreaks } from '../../common/sanitizeHtml';
import { handleReportFileLinkClick } from '../../common/Utils';
import { MESSAGES } from '../../common/Messages';
import {
  AIUsageResultKinds,
  AIUsageBuckets,
  AIUsageLlmErrorKinds,
  AIUsageFeedbackActions,
  AIUsageVerdicts,
  AIUsageFlags,
  AIUsageResolutionSources,
  AIUsageTopics,
} from '../../const/Lookup';
import SearchLayout from './UsageTurnsSearchLayout';
import { USAGE_OVERLAYS, USAGE_TILES, buildFilters, deltaOf, initialSearchFields } from './usageMetrics';
import '../../common/css/searchPage.css';
import './usageDashboard.css';

// Read-only screen. CLAUDE.md rules 4, 14, 15 and 16 (inline-edit grids, concurrency-aware stores) are
// conditioned on grid-originated writes, and this grid never writes - so none of that machinery applies.

// Fixed colours for data, never the brand colour: Color2 is a red at some tenants, which would paint a
// success rate red. Brand colour is used for the heading only.
const COLORS = {
  AnsweredWithoutData: '#e05c3c',
  UnsupportedRequest: '#c77dbb',
  NoRecordsFound: '#e08a3c',
  NeedsInput: '#7e8fd4',
  FoundButFieldEmpty: '#8e6bb5',
  FieldNotAvailable: '#5b9bb0',
  Error: '#d9534f',
  LlmError: '#a02c2c',
  NoAnswer: '#b5462f',
  UserDisliked: '#6d6d6d',
};
const FALLBACK_COLOR = '#9e9e9e';

// The codes the API returns, resolved to their descriptions from the shared lookups - the same lists the
// grid's Lookup columns and the search panel use, so nothing can label one code two ways.
const textOf = (lookup) => {
  const byKey = lookup.reduce((map, option) => ({ ...map, [option.key]: option.value }), {});
  return (code) => byKey[code] || code;
};
const label = textOf(AIUsageBuckets);
const llmLabel = textOf(AIUsageLlmErrorKinds);

// Search-panel fields shown as their lookup's description in the "why is this filtered" line.
const FIELD_TEXT = {
  Bucket: { caption: 'Result', text: label },
  Verdict: { caption: 'Verdict', text: textOf(AIUsageVerdicts) },
  Flag: { caption: 'Signal', text: textOf(AIUsageFlags) },
  Topic: { caption: 'Topic', text: textOf(AIUsageTopics) },
  UserName: { caption: 'User', text: (v) => v },
  Prompt: { caption: 'Prompt contains', text: (v) => `"${v}"` },
};

// The search panel's selects and the lookup each one offers.
const SELECT_OPTIONS = {
  Bucket: AIUsageBuckets,
  Verdict: AIUsageVerdicts,
  Flag: AIUsageFlags,
  Topic: AIUsageTopics,
};

const searchpanel = [{ ID: 1, Title: 'Search Filters' }];

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d, days) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
const startOfMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};
const formatDay = (d) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

// The browser is the only place that knows the user's time zone, so the range is converted here: from
// the start of the first local day to the end of the last. Sending bare dates would shift every turn near
// midnight into the wrong day by the server's offset.
const toRangeParams = (from, to) => {
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate(), 0, 0, 0, 0);
  const end = new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999);
  return { FromUtc: start.toISOString(), ToUtc: end.toISOString() };
};

// The period of the same length that ends the day before this one starts - what every delta compares to.
const previousPeriod = (from, to) => {
  const days = Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS) + 1;
  return { from: addDays(from, -days), to: addDays(from, -1) };
};

// Where the screen starts: always the current month - a changed range is never saved - with any applied
// search read back from the query string (a pasted link, or F5).
const readInitialState = (search) => {
  const params = new URLSearchParams(search || '');
  const from = startOfMonth();
  const to = startOfDay(new Date());

  const fields = Object.keys(initialSearchFields)
    .reduce((acc, name) => ({ ...acc, [name]: params.get(name) || '' }), {});

  return { from, to, fields };
};

const hasAnyField = (fields) => Object.keys(initialSearchFields).some((name) => !!fields[name]);

// "No data" and "0%" are opposite conclusions, so an empty denominator shows a dash, never 0%.
const pct = (rate) => (rate && rate.Value != null ? `${Math.round(rate.Value * 100)}%` : '—');
const ratio = (rate, noun) => (rate ? `${rate.Numerator} / ${rate.Denominator} ${noun}` : '');

const DeltaChip = ({ delta, versus }) => {
  if (!delta) return null;

  const tone = delta.good == null ? 'neutral' : delta.good ? 'good' : 'bad';
  const arrow = delta.change > 0 ? '▲' : delta.change < 0 ? '▼' : '■';
  const amount = `${Math.abs(delta.change)}${delta.unit}`;

  return (
    <span className={`usage-delta usage-delta--${tone}`} title={`Versus ${versus}`}>
      {arrow} {amount}
    </span>
  );
};

const Tile = ({ tile, metrics, previous, versus, onDrill }) => {
  const rate = metrics[tile.key];
  const value = tile.kind === 'ratio'
    ? (rate != null ? rate.toFixed(1) : '—')
    : pct(rate);
  const detail = tile.kind === 'ratio'
    ? `${metrics.Scored} / ${metrics.Delivered}`
    : ratio(rate, tile.noun);
  const alert = tile.alertWhenNonZero && rate && rate.Numerator > 0;

  // How many turns the drill would list. A rate tile lists its own numerator; the one ratio tile has no
  // count of its own and drills to the failures. Zero means there is nothing to show, so the tile is not
  // offered as a link - clicking it used to apply a filter that emptied the grid and then stayed applied,
  // which reads as a broken screen rather than as an empty result.
  const drillCount = tile.kind === 'ratio' ? metrics.Failures : (rate ? rate.Numerator : 0);
  const clickable = drillCount > 0;

  const classes = ['usage-tile'];
  if (clickable) classes.push('usage-tile--clickable');
  if (tile.row === 'context' && !alert) classes.push('usage-tile--context');
  if (alert) classes.push('usage-tile--alert');

  return (
    <div
      className={classes.join(' ')}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={tile.tooltip}
      onClick={clickable ? () => onDrill(tile.drill) : undefined}
      onKeyDown={clickable
        ? (e) => { if (e.key === 'Enter' || e.key === ' ') onDrill(tile.drill); }
        : undefined}
    >
      <div className="usage-tile__caption">
        {tile.caption}
        {tile.fromFeedback ? <span className="usage-tile__feedback" title="From user feedback">●</span> : null}
      </div>
      <div className="usage-tile__value">
        {value} <DeltaChip delta={deltaOf(tile, metrics, previous)} versus={versus} />
      </div>
      <div className="usage-tile__detail">{detail}</div>
    </div>
  );
};

const TurnDetail = ({ data: row }) => {
  // Each row is the stored turn plus its classification; the exception is an ABAPILOGS column of its own.
  const { Turn: turn = {}, Exception: exception } = row.data;
  // A grid detail template gets only the row, so the token comes from the store.
  const accessToken = useSelector((state) => state.oidc.user?.access_token);
  return (
    <div className="usage-detail">
      <div className="usage-detail__label">Prompt</div>
      <pre className="usage-detail__text">{turn.Prompt || '—'}</pre>

      <div className="usage-detail__label">Answer shown to the customer</div>
      {/* The answer is HTML - rendered exactly as the chat renders it, not shown as raw tags. */}
      <div className="usage-detail__html" dangerouslySetInnerHTML={{ __html: sanitizeHtml(withLineBreaks(turn.Answer || '')) }} onClick={(e) => handleReportFileLinkClick(e, accessToken)} />

      <div className="usage-detail__label">Tool calls</div>
      {turn.ToolCalls && turn.ToolCalls.length > 0 ? (
        turn.ToolCalls.map((call, index) => (
          <pre key={index} className="usage-detail__text">
            {`${call.Name || '(no call)'}  ·  ${call.Kind ? label(call.Kind) : 'not recorded'}\n`}
            {`Arguments: ${call.Arguments || '—'}\n`}
            {`Result: ${call.Result || '—'}`}
            {call.Detail ? `\nDetail: ${call.Detail}` : ''}
            {call.Exception ? `\nException: ${call.Exception}` : ''}
          </pre>
        ))
      ) : (
        <pre className="usage-detail__text">(none — no tool was called)</pre>
      )}

      {turn.LlmFailure ? (
        <>
          <div className="usage-detail__label">LLM failure</div>
          <pre className="usage-detail__text usage-detail__text--error">
            {`${turn.LlmFailure.ExceptionType || ''}  ·  status ${turn.LlmFailure.StatusCode ?? 'none'}\n`}
            {turn.LlmFailure.Message || ''}
            {turn.LlmFailure.ResponseBody ? `\n${turn.LlmFailure.ResponseBody}` : ''}
          </pre>
        </>
      ) : null}

      {turn.GuardWithheldText ? (
        <>
          <div className="usage-detail__label">Withheld from the customer by the output guard</div>
          <pre className="usage-detail__text usage-detail__text--error">{turn.GuardWithheldText}</pre>
        </>
      ) : null}

      {turn.CoverageViolation ? (
        <>
          <div className="usage-detail__label">Coverage violation</div>
          <pre className="usage-detail__text">{turn.CoverageViolation}</pre>
        </>
      ) : null}

      {turn.Diagnostic ? (
        <>
          <div className="usage-detail__label">Diagnostic</div>
          <pre className="usage-detail__text">{turn.Diagnostic}</pre>
        </>
      ) : null}

      {exception ? (
        <>
          <div className="usage-detail__label">Full exception</div>
          <pre className="usage-detail__text usage-detail__text--error">{exception}</pre>
        </>
      ) : null}
    </div>
  );
};

const UsageDashboard = (props) => {
  const accessToken = props.oidc.user.access_token;
  const currentUser = props.oidc.user;
  const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
  const history = useHistory();
  const location = useLocation();
  const dataGrid = useRef(null);
  const formRefs = useRef({});

  const [initial] = useState(() => readInitialState(location.search));

  // The range the figures and the grid are read for - it changes only on Search or "This month". The date
  // boxes edit the draft, so a half-edited range is never loaded. Neither is saved anywhere.
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [draftFrom, setDraftFrom] = useState(initial.from);
  const [draftTo, setDraftTo] = useState(initial.to);
  const [metrics, setMetrics] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // What the search panel shows, and what the grid is filtered by - the latter changes only on Search,
  // Reset or a click on a figure, exactly as on every other search screen.
  const [searchFields, setSearchFields] = useState({ ...initialSearchFields, ...initial.fields });
  const [appliedFields, setAppliedFields] = useState({ ...initialSearchFields, ...initial.fields });
  const [selectedItems, setSelectedItems] = useState(hasAnyField(initial.fields) ? [searchpanel[0]] : []);

  const gridKey = 'a7c3e915-4d2b-4f6a-9e81-3b5d0c7f2a64';
  const [currentLayout, setCurrentLayout] = useState('Default');
  const loadState = useCallback(
    () => GridLayout.sendStorageRequest(gridKey, 'GET', accessToken, null, currentLayout),
    [accessToken, currentLayout]
  );
  const saveState = useCallback(
    (state) => { GridLayout.sendStorageRequest(gridKey, 'POST', accessToken, state, currentLayout); },
    [accessToken, currentLayout]
  );

  const rangeParams = useMemo(() => toRangeParams(from, to), [from, to]);
  const previousRange = useMemo(() => previousPeriod(from, to), [from, to]);
  const headers = useMemo(
    () => ({ access_token: accessToken, Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  // The applied search travels in the query string, so it survives F5. The range does not: it is always
  // the current month on arrival.
  useEffect(() => {
    const params = new URLSearchParams();
    Object.keys(initialSearchFields).forEach((name) => {
      if (appliedFields[name]) params.set(name, appliedFields[name]);
    });
    const query = params.toString();
    const search = query ? `?${query}` : '';
    if (search !== location.search) history.replace({ pathname: location.pathname, search });
  }, [appliedFields, history, location.pathname, location.search]);

  useEffect(() => {
    let cancelled = false;
    const url = `${window._env_.REACT_APP_APIS}/${data.AIUsage.getUsageMetrics}`;
    setLoading(true);
    setErrorMessage('');

    axios
      .get(url, { params: rangeParams, headers })
      .then((res) => { if (!cancelled) setMetrics(res.data); })
      .catch((err) => {
        if (cancelled) return;
        // The API's own message when there is one (e.g. the 403 for a role without the permission).
        const serverMessage = typeof err.response?.data === 'string' ? err.response.data : null;
        setErrorMessage(serverMessage || err.message);
        setMetrics(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    // The previous period only feeds the delta chips, so its failure costs the chips and nothing else.
    axios
      .get(url, { params: toRangeParams(previousRange.from, previousRange.to), headers })
      .then((res) => { if (!cancelled) setPrevious(res.data); })
      .catch(() => { if (!cancelled) setPrevious(null); });

    return () => { cancelled = true; };
  }, [rangeParams, previousRange, headers]);

  // Rebuilt whenever the range or the applied search changes. It always lists the range - every turn,
  // delivered ones included - so a period's delivery rate can be re-derived by hand from the grid.
  const turnsSource = useMemo(() => {
    const store = createStore({
      key: 'Turn.TurnId',
      loadParams: rangeParams,
      loadUrl: `${window._env_.REACT_APP_APIS}/${data.AIUsage.getUsageTurns}`,
      onBeforeSend: (method, ajaxOptions) => { ajaxOptions.headers = headers; },
    });
    const filter = buildFilters(appliedFields);
    return new DataSource(filter ? { store, filter } : { store });
  }, [appliedFields, rangeParams, headers]);

  // A click on a figure: fill the search panel with what it counts and run the search. The panel stays open or
  // closed as the user left it.
  const drill = useCallback((fields) => {
    const next = { ...initialSearchFields, ...(fields || {}) };
    setSearchFields(next);
    setAppliedFields(next);
  }, []);

  const handleSearch = (e) => {
    e.event.stopPropagation();
    setAppliedFields({ ...searchFields });
  };

  const handleReset = (e) => {
    e.event.stopPropagation();
    setSearchFields(initialSearchFields);
    setAppliedFields(initialSearchFields);
  };

  const handleFieldChange = useCallback(() => {
    // No field-level business rules on a read-only search.
  }, []);

  const handleFormLoad = useCallback(() => {
    Object.keys(SELECT_OPTIONS).forEach((name) => {
      const field = formRefs.current[name];
      if (!field) return;
      const instance = field.instance();
      instance.option('dataSource', SELECT_OPTIONS[name].map((o) => ({ id: o.key, name: o.value })));
      instance.option('valueExpr', 'id');
      instance.option('displayExpr', 'name');
    });
  }, []);

  const selectionChanged = useCallback((e) => {
    let newItems = [...selectedItems];
    e.removedItems.forEach((item) => {
      const index = newItems.indexOf(item);
      if (index >= 0) newItems.splice(index, 1);
    });
    if (e.addedItems.length) newItems = [...newItems, ...e.addedItems];
    setSelectedItems(newItems);
  }, [selectedItems]);

  // Copies are applied, so Search on an unchanged range still reloads - it doubles as a refresh.
  const applyRange = (nextFrom, nextTo) => {
    setDraftFrom(nextFrom);
    setDraftTo(nextTo);
    setFrom(new Date(nextFrom));
    setTo(new Date(nextTo));
  };

  // A cleared box is an empty draft, refused here - never silently replaced by the date it last held.
  const handleRangeSearch = () => {
    if (!draftFrom || !draftTo) {
      notify({ message: MESSAGES.DATE_RANGE_REQUIRED, width: 100 }, 'error', 3000);
      return;
    }
    if (draftFrom > draftTo) {
      notify({ message: MESSAGES.FROM_DATE_AFTER_TO_DATE, width: 100 }, 'error', 3000);
      return;
    }
    applyRange(draftFrom, draftTo);
  };

  // "This month" is the reset: it applies the default range at once.
  const resetToThisMonth = () => applyRange(startOfMonth(), startOfDay(new Date()));

  const failureReasons = metrics?.FailureReasons || [];
  const maxFailure = failureReasons.reduce((max, b) => Math.max(max, b.Count), 1);
  const isThisMonth = from.getTime() === startOfMonth().getTime()
    && to.toDateString() === new Date().toDateString();
  const versus = `${formatDay(previousRange.from)} – ${formatDay(previousRange.to)}`;

  const appliedSummary = Object.keys(initialSearchFields)
    .filter((name) => appliedFields[name])
    .map((name) => `${FIELD_TEXT[name].caption}: ${FIELD_TEXT[name].text(appliedFields[name])}`)
    .join(' · ');

  // A count of zero has nothing to list, so it is plain text rather than a link that appears to do nothing.
  const DrillLink = ({ fields, count, children }) => (count === 0 ? <span>{children}</span> : (
    <span
      role="button"
      tabIndex={0}
      className="usage-link"
      onClick={() => drill(fields)}
      onKeyDown={(e) => { if (e.key === 'Enter') drill(fields); }}
    >
      {children}
    </span>
  ));

  return (
    <div className="search-page-container">
      <div className="search-page-content usage-page">
        <Toolbar className="header">
          <Item location="before">
            <div className="title">
              <h6 style={{ color: window._env_.Color2 }}>Usage Metrics</h6>
            </div>
          </Item>
          <Item location="after">
            <DateBox value={draftFrom} type="date" width={140} onValueChanged={(e) => setDraftFrom(e.value ? startOfDay(e.value) : null)} />
          </Item>
          <Item location="after">
            <span className="usage-range__to">to</span>
          </Item>
          <Item location="after">
            <DateBox value={draftTo} type="date" width={140} onValueChanged={(e) => setDraftTo(e.value ? startOfDay(e.value) : null)} />
          </Item>
          <Item location="after">
            <Button className="accordianButton" text="Search" type="default" onClick={handleRangeSearch} />
          </Item>
          <Item location="after">
            <Button className="accordianButton" text="This month" onClick={resetToThisMonth} />
          </Item>
        </Toolbar>

        {/* The figures' own loader stays inside this section; the grid below shows its own. */}
        <div id="usage-figures" className={`usage-figures${loading && !metrics ? ' usage-figures--loading' : ''}`}>
          <LoadPanel
            shadingColor="rgba(0,0,0,0.4)"
            visible={loading}
            showIndicator={true}
            shading={true}
            showPane={true}
            hideOnOutsideClick={false}
            container="#usage-figures"
            position={{ of: '#usage-figures' }}
          />

          {metrics ? (
            <div className="usage-summary">
              {formatDay(from)} – {formatDay(to)}
              {isThisMonth ? ' (month to date)' : ''}
              {' · '}
              <DrillLink fields={null} count={metrics.TotalTurns}>{metrics.TotalTurns} turns logged</DrillLink>
              {' · '}
              <DrillLink fields={{ Verdict: 'Success' }} count={metrics.Delivered}>{metrics.Delivered} delivered</DrillLink>
              {' + '}
              <DrillLink fields={{ Verdict: 'Failure' }} count={metrics.Failures}>{metrics.Failures} failures</DrillLink>
              {' + '}
              <DrillLink fields={{ Verdict: 'Excluded' }} count={metrics.Excluded}>{metrics.Excluded} excluded</DrillLink>
              {metrics.Truncated ? ' · showing the most recent turns only — narrow the range for complete figures' : ''}
            </div>
          ) : null}

          {errorMessage ? <div className="usage-error">{errorMessage}</div> : null}

          {metrics && metrics.UnreadableRows > 0 ? (
            <div className="usage-error">
              {metrics.UnreadableRows} row(s) in this range could not be read and are not counted in any figure.
            </div>
          ) : null}

          {metrics ? (
            <>
              <div className="usage-tile-groups">
                {[
                  { row: 'quality', label: 'Quality — over scored turns (delivered + failures)' },
                  { row: 'context', label: 'Context — divided by all turns, not quality measures' },
                ].map(({ row, label: caption }) => {
                  const tiles = USAGE_TILES.filter((t) => t.row === row);
                  return (
                    <div key={row} className="usage-tile-group" style={{ '--tile-count': tiles.length }}>
                      <div className="usage-section-label">{caption}</div>
                      <div className="usage-tiles">
                        {tiles.map((tile) => (
                          <Tile key={tile.key} tile={tile} metrics={metrics} previous={previous} versus={versus} onDrill={drill} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="usage-section-label usage-section-label--strong">Failure reasons</div>
              <div className="usage-summary">
                As a share of the {metrics.Scored} scored turns — click a row to see those turns
              </div>
              {failureReasons.length === 0 ? (
                <div className="usage-summary">No failures in this range.</div>
              ) : (
                failureReasons.map((bucket) => (
                  <div
                    key={bucket.Kind}
                    role="button"
                    tabIndex={0}
                    className={`usage-bar${appliedFields.Bucket === bucket.Kind ? ' usage-bar--selected' : ''}`}
                    onClick={() => drill({ Bucket: bucket.Kind })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') drill({ Bucket: bucket.Kind }); }}
                  >
                    <div className="usage-bar__label">{label(bucket.Kind)}</div>
                    <div className="usage-bar__track">
                      <div
                        className="usage-bar__fill"
                        style={{ width: `${(bucket.Count / maxFailure) * 100}%`, background: COLORS[bucket.Kind] || FALLBACK_COLOR }}
                      />
                    </div>
                    <div className="usage-bar__value">
                      {metrics.Scored ? Math.round((bucket.Count / metrics.Scored) * 100) : 0}% ({bucket.Count})
                    </div>
                  </div>
                ))
              )}

              <div className="usage-summary usage-overlays">
                Beside the breakdown, not failure buckets:{' '}
                {USAGE_OVERLAYS.map((overlay, i) => (
                  <React.Fragment key={overlay.key}>
                    {i > 0 ? ' · ' : ''}
                    <DrillLink fields={overlay.drill} count={metrics[overlay.key] ? metrics[overlay.key].Numerator : 0}>
                      {overlay.caption} {pct(metrics[overlay.key])} ({ratio(metrics[overlay.key], overlay.noun)})
                    </DrillLink>{' '}
                    <DeltaChip delta={deltaOf(overlay, metrics, previous)} versus={versus} />
                  </React.Fragment>
                ))}
              </div>

              {metrics.LlmErrorKinds && metrics.LlmErrorKinds.length > 0 ? (
                <div className="usage-summary">
                  LLM errors: {metrics.LlmErrorKinds.map((b) => `${llmLabel(b.Kind)} ${b.Count}`).join(' · ')}
                </div>
              ) : null}

              {metrics.ExcludedReasons && metrics.ExcludedReasons.length > 0 ? (
                <div className="usage-summary">
                  Excluded, not failures:{' '}
                  {metrics.ExcludedReasons.map((b, i) => (
                    <React.Fragment key={b.Kind}>
                      {i > 0 ? ' · ' : ''}
                      <DrillLink fields={{ Bucket: b.Kind }} count={b.Count}>{label(b.Kind)} ({b.Count})</DrillLink>
                    </React.Fragment>
                  ))}
                </div>
              ) : null}

              {metrics.OrphanFeedback > 0 ? (
                <div className="usage-summary">
                  {metrics.OrphanFeedback} rating(s) on answers from before the usage log existed could not be joined to a turn.
                </div>
              ) : null}
            </>
          ) : null}
        </div>

        <div className="usage-drill">
          <div className="usage-section-label usage-section-label--strong">
            Turns{appliedSummary ? ` — ${appliedSummary}` : ' — all in range'}
          </div>

          <div id="accordion" className="search-page-accordion">
            <AccordionReact
              dataSource={searchpanel}
              collapsible={true}
              multiple={false}
              animationDuration={300}
              selectedItems={selectedItems}
              onSelectionChanged={(e) => {
                if (e.currentTarget === e.target) {
                  selectionChanged(e);
                }
              }}
              itemTitleRender={() => (
                <Toolbar className="accordian-toolbar">
                  <Item location="before">
                    <div className="title">
                      <h6 style={{ color: window._env_.Color2 }}>Search Turns</h6>
                    </div>
                  </Item>
                  <Item location="after">
                    <Button className="accordianButton" onClick={handleSearch} text="Search" type="default" />
                  </Item>
                  <Item location="after">
                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                  </Item>
                </Toolbar>
              )}
              itemRender={() => (
                <FormField
                  currrentUser={currentUser}
                  layoutId="5d2f8c1a-9b3e-4f67-a1c4-7e8b2d6f9a03"
                  layoutName="UsageTurnsSearchForm"
                  defaultFormSchema={SearchLayout}
                  formFields={searchFields}
                  setformFields={setSearchFields}
                  handleFieldValueChange={handleFieldChange}
                  DYNAMICFORMSETTING={DYNAMICFORMSETTING}
                  formRefs={formRefs}
                  onFormLoad={handleFormLoad}
                  noSpacingBeforeFirstRow={true}
                />
              )}
              id="accordion-container" className="accordion-height"
            />
          </div>

          <div className="search-page-grid-container">
            <DataGrid
              ref={dataGrid}
              dataSource={turnsSource}
              remoteOperations={true}
              showBorders={true}
              hoverStateEnabled={true}
              allowColumnReordering={true}
              allowColumnResizing={true}
              columnAutoWidth={true}
              className="search-page-grid"
              onExporting={(e) => { if (window.handleDevExtremeDataGridExport) window.handleDevExtremeDataGridExport(e); }}
            >
              <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={gridKey} customSave={saveState} />
              <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
              </ColumnChooser>
              <Export enabled={true} allowExportSelectedData={true} />
              <Sorting mode="multiple" />
              <Paging defaultPageSize={20} />
              <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[10, 20, 100, 500]} showNavigationButtons={true} />
              <FilterRow visible={true} />
              <HeaderFilter visible={true} />
              <MasterDetail enabled={true} component={TurnDetail} />

              <Column dataField="RecordNumber" caption="Record #" dataType="number" allowEditing={false} width={90} />
              <Column dataField="Turn.RequestStartedUtc" caption="Time" dataType="datetime" format="MM/dd/yyyy hh:mm:ss aa" defaultSortOrder="desc" />
              <Column dataField="UserName" caption="User" />
              <Column dataField="Turn.Prompt" caption="Prompt" />
              <Column dataField="Classification.Verdict" caption="Verdict">
                <Lookup dataSource={AIUsageVerdicts} valueExpr="key" displayExpr="value" />
              </Column>
              <Column dataField="Classification.Kind" caption="Result">
                <Lookup dataSource={AIUsageResultKinds} valueExpr="key" displayExpr="value" />
              </Column>
              <Column dataField="Classification.LlmErrorKind" caption="LLM error">
                <Lookup dataSource={AIUsageLlmErrorKinds} valueExpr="key" displayExpr="value" />
              </Column>
              <Column dataField="Classification.FeedbackAction" caption="Feedback">
                <Lookup dataSource={AIUsageFeedbackActions} valueExpr="key" displayExpr="value" />
              </Column>
              <Column dataField="Classification.ResolutionSource" caption="Judged by">
                <Lookup dataSource={AIUsageResolutionSources} valueExpr="key" displayExpr="value" />
              </Column>
              <Column dataField="Classification.Topic" caption="Topic">
                <Lookup dataSource={AIUsageTopics} valueExpr="key" displayExpr="value" />
              </Column>
              <Column dataField="Classification.MatchedReport" caption="Report asked for" />
              <Column dataField="Classification.IdentifierFriction" caption="Identifier friction" dataType="boolean" />
              <Column dataField="Classification.CoverageViolation" caption="Coverage violation" dataType="boolean" />
              <Column dataField="Classification.Repaired" caption="Repaired" dataType="boolean" />
              <Column dataField="Classification.DeliveredButDisliked" caption="Delivered but disliked" dataType="boolean" visible={false} />
              <Column dataField="ToolCallCount" caption="Tool calls" dataType="number" />
              <Column dataField="Turn.DurationMs" caption="Duration (ms)" dataType="number" />
              <Column dataField="Turn.Model" caption="Model" visible={false} />
              <Column dataField="Turn.TurnId" caption="Turn Id" visible={false} />
              <Column dataField="Turn.ConversationId" caption="Conversation Id" visible={false} />

              <GridToolbar>
                <GridToolbarItem location="after">
                  <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                </GridToolbarItem>
                <GridToolbarItem name="exportButton" />
                <GridToolbarItem name="columnChooserButton" />
              </GridToolbar>
            </DataGrid>
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    oidc: state.oidc,
  };
}

export default connect(mapStateToProps)(UsageDashboard);
